import type { ChatLanguage } from '@oneapp/shared';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from '../locales/en.json';
import hi from '../locales/hi.json';

/** Every string the app shows lives in locales/. Missing Hindi strings fall back to English. */
export const resources = { en: { translation: en }, hi: { translation: hi } } as const;

const STORAGE_KEY = 'oneapp:lang';

function savedLanguage(): ChatLanguage | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === 'en' || value === 'hi' ? value : null;
  } catch {
    return null;
  }
}

/** Before sign-in: the language chosen on this phone, else the phone's own language. */
function startLanguage(): ChatLanguage {
  const saved = typeof window === 'undefined' ? null : savedLanguage();
  if (saved) return saved;
  return typeof navigator !== 'undefined' && navigator.language?.toLowerCase().startsWith('hi') ? 'hi' : 'en';
}

function markDocument(language: ChatLanguage) {
  if (typeof document !== 'undefined') document.documentElement.lang = language === 'hi' ? 'hi-IN' : 'en-IN';
}

/** Switch the app's language and remember it on this phone. */
export async function applyLanguage(language: ChatLanguage): Promise<void> {
  try {
    window.localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // storage can be blocked; the choice then lasts for this visit only
  }
  markDocument(language);
  if (i18n.language !== language) await i18n.changeLanguage(language);
}

export const currentLanguage = (): ChatLanguage => (i18n.language === 'hi' ? 'hi' : 'en');

const initial = startLanguage();
markDocument(initial);
void i18n.use(initReactI18next).init({
  resources,
  lng: initial,
  fallbackLng: 'en',
  interpolation: { escapeValue: false }, // React already escapes
  returnNull: false,
});

export default i18n;
